import React, { useCallback, useEffect, useRef, useState } from "react";
import { ColorPicker, Tooltip } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  FontColorsOutlined,
  HighlightOutlined,
  FontSizeOutlined,
} from "@ant-design/icons";
import { newsBodyHasText, sanitizeNewsHtml } from "./sanitizeNewsHtml";

const defaultFormats = {
  bold: false,
  italic: false,
  underline: false,
  p: true,
  h1: false,
  h2: false,
  h3: false,
  ul: false,
  ol: false,
};

const BLOCK_TAGS = ["h1", "h2", "h3", "p", "div"];

const getBlockElement = (editor) => {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !editor) {
    return null;
  }
  let node = selection.anchorNode;
  if (node?.nodeType === Node.TEXT_NODE) {
    node = node.parentElement;
  }
  while (node && node !== editor) {
    const tag = node.tagName?.toLowerCase();
    if (BLOCK_TAGS.includes(tag)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const getBlockTag = (editor) => {
  const fromDom = getBlockElement(editor)?.tagName?.toLowerCase() || "";
  if (fromDom) {
    return fromDom;
  }
  return String(document.queryCommandValue("formatBlock") || "")
    .toLowerCase()
    .replace(/[<>]/g, "");
};

const ToolboxButton = ({ title, onClick, active, children }) => (
  <Tooltip title={title}>
    <button
      type="button"
      className={`feature-news-toolbox-btn${active ? " is-active" : ""}`}
      aria-pressed={active ? "true" : "false"}
      onMouseDown={(event) => {
        event.preventDefault();
        onClick();
      }}
    >
      {children}
    </button>
  </Tooltip>
);

const FeatureNewsEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const skipValueSync = useRef(false);
  const [focused, setFocused] = useState(false);
  const [formats, setFormats] = useState(defaultFormats);
  const isEmpty = !newsBodyHasText(value);

  const refreshFormats = useCallback(() => {
    if (typeof document.queryCommandState !== "function") {
      return;
    }
    const block = getBlockTag(editorRef.current);
    setFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      p: !["h1", "h2", "h3"].includes(block),
      h1: block === "h1",
      h2: block === "h2",
      h3: block === "h3",
      ul: document.queryCommandState("insertUnorderedList"),
      ol: document.queryCommandState("insertOrderedList"),
    });
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", refreshFormats);
    return () => document.removeEventListener("selectionchange", refreshFormats);
  }, [refreshFormats]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) {
      return;
    }
    if (skipValueSync.current) {
      skipValueSync.current = false;
      return;
    }
    const next = value || "";
    if (el.innerHTML !== next) {
      el.innerHTML = next;
    }
  }, [value]);

  const emitChange = () => {
    skipValueSync.current = true;
    const html = sanitizeNewsHtml(editorRef.current?.innerHTML || "");
    onChange?.(html);
    refreshFormats();
  };

  const run = (command, commandValue, useCss = false) => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    if (document.activeElement !== editor) {
      editor.focus();
    }
    document.execCommand("styleWithCSS", false, Boolean(useCss));
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  const applyBlockFormat = (tag) => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    if (document.activeElement !== editor) {
      editor.focus();
    }
    document.execCommand("styleWithCSS", false, false);

    const current = getBlockTag(editor);
    const nextTag = current === tag ? "p" : tag;

    const formatted = document.execCommand("formatBlock", false, `<${nextTag}>`);
    if (!formatted) {
      document.execCommand("formatBlock", false, nextTag);
    }

    const block = getBlockElement(editor);
    if (block && editor.contains(block) && block.tagName.toLowerCase() !== nextTag) {
      const replacement = document.createElement(nextTag);
      replacement.innerHTML = block.innerHTML || "<br>";
      block.replaceWith(replacement);
      const range = document.createRange();
      range.selectNodeContents(replacement);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    emitChange();
  };

  return (
    <div className={`feature-news-editor${focused ? " is-focused" : ""}`}>
      <div className="feature-news-toolbox" role="toolbar" aria-label="Body formatting">
        <div className="feature-news-toolbox-group" aria-label="Headings">
          <span className="feature-news-toolbox-label">
            <FontSizeOutlined />
          </span>
          <ToolboxButton title="Normal text" active={formats.p} onClick={() => applyBlockFormat("p")}>
            P
          </ToolboxButton>
          <ToolboxButton title="Heading 1" active={formats.h1} onClick={() => applyBlockFormat("h1")}>
            H1
          </ToolboxButton>
          <ToolboxButton title="Heading 2" active={formats.h2} onClick={() => applyBlockFormat("h2")}>
            H2
          </ToolboxButton>
          <ToolboxButton title="Heading 3" active={formats.h3} onClick={() => applyBlockFormat("h3")}>
            H3
          </ToolboxButton>
        </div>
        <div className="feature-news-toolbox-group" aria-label="Text style">
          <ToolboxButton title="Bold" active={formats.bold} onClick={() => run("bold")}><BoldOutlined /></ToolboxButton>
          <ToolboxButton title="Italic" active={formats.italic} onClick={() => run("italic")}><ItalicOutlined /></ToolboxButton>
          <ToolboxButton title="Underline" active={formats.underline} onClick={() => run("underline")}><UnderlineOutlined /></ToolboxButton>
        </div>
        <div className="feature-news-toolbox-group" aria-label="Colour">
          <Tooltip title="Text colour">
            <span className="feature-news-toolbox-picker">
              <FontColorsOutlined />
              <ColorPicker
                size="small"
                defaultValue="#FF7100"
                onChangeComplete={(color) => run("foreColor", color.toHexString(), true)}
              />
            </span>
          </Tooltip>
          <Tooltip title="Highlight">
            <span className="feature-news-toolbox-picker">
              <HighlightOutlined />
              <ColorPicker
                size="small"
                defaultValue="#FFE7BA"
                onChangeComplete={(color) => run("hiliteColor", color.toHexString(), true)}
              />
            </span>
          </Tooltip>
        </div>
        <div className="feature-news-toolbox-group" aria-label="Lists">
          <ToolboxButton title="Bullets" active={formats.ul} onClick={() => run("insertUnorderedList")}>
            <UnorderedListOutlined />
          </ToolboxButton>
          <ToolboxButton title="Numbered list" active={formats.ol} onClick={() => run("insertOrderedList")}>
            <OrderedListOutlined />
          </ToolboxButton>
        </div>
      </div>
      <div
        ref={editorRef}
        className={`feature-news-editor-surface${isEmpty ? " is-empty" : ""}`}
        contentEditable
        suppressContentEditableWarning
        data-placeholder="Write the announcement. Use P for normal size, or H1 / H2 / H3 for headings."
        onInput={emitChange}
        onKeyUp={refreshFormats}
        onMouseUp={refreshFormats}
        onFocus={() => {
          setFocused(true);
          refreshFormats();
        }}
        onBlur={() => {
          setFocused(false);
          emitChange();
        }}
      />
    </div>
  );
};

export default FeatureNewsEditor;
